import React, { createContext, useContext, useState } from 'react';

const StudyContext = createContext();

// Helper function to shuffle an array
function shuffleArray(array) {
  const shuffled = [...array];
  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
  }
  return shuffled;
}

// Dynamically load all JSON files from the data directory
function loadAllDataSets() {
  const context = require.context('../data', false, /\.json$/);
  const dataSets = [];
  
  context.keys().forEach(key => {
    const dataSet = context(key);
    dataSets.push(dataSet);
  });
  
  return dataSets;
}

// Load data sets synchronously at module load time
const initialDataSets = loadAllDataSets();

export function StudyProvider({ children }) {
  const [studySets, setStudySets] = useState(initialDataSets);

  // Get summary of all study sets
  const getStudySetsSummary = () => {
    return studySets.map(set => ({
      id: set.id,
      title: set.title,
      description: set.description,
      questionCount: set.questions.length,
      categories: set.categories || [],
      difficulty: set.difficulty
    }));
  };

  // Get a specific study set
  const getStudySet = (id) => {
    return studySets.find(set => set.id === id);
  };

  // Get categories for a study set
  const getCategories = (studySetId) => {
    const studySet = studySets.find(set => set.id === studySetId);
    if (!studySet) return { categories: [], totalQuestions: 0 };

    // Extract unique categories from questions
    const categoryNames = [...new Set(studySet.questions.map(q => q.category).filter(Boolean))];
    
    // Count questions per category
    const categoryCounts = categoryNames.map(category => ({
      name: category,
      count: studySet.questions.filter(q => q.category === category).length
    }));

    return {
      categories: categoryCounts,
      totalQuestions: studySet.questions.length
    };
  };

  // Create a study session
  const createStudySession = (studySetId, mode, questionCount, categories) => {
    const studySet = studySets.find(set => set.id === studySetId);
    if (!studySet) return null;

    let questions = [...studySet.questions];
    
    // Filter by categories if specified
    if (categories && categories.length > 0) {
      questions = questions.filter(q => categories.includes(q.category));
    }
    
    // Filter out questions without choices if mode is multiple-choice
    // This prevents unanswerable questions from appearing in the session
    if (mode === 'multiple-choice') {
      questions = questions.filter(q => q.choices && q.choices.length > 0);
    }
    
    // Shuffle questions
    questions = shuffleArray(questions);
    
    // Limit number of questions if specified
    if (questionCount && questionCount > 0) {
      questions = questions.slice(0, questionCount);
    }

    // Format questions based on mode
    const formattedQuestions = questions.map(q => {
      const formatted = {
        id: q.id,
        question: q.question,
        category: q.category,
        difficulty: q.difficulty
      };

      if (mode === 'beginner' && q.hint) {
        formatted.hint = q.hint;
      }

      if (mode === 'multiple-choice' && q.choices) {
        // Randomize the order of answer choices
        formatted.choices = shuffleArray(q.choices);
      }

      return formatted;
    });

    return {
      sessionId: Date.now().toString(),
      mode,
      questions: formattedQuestions,
      totalQuestions: formattedQuestions.length,
      selectedCategories: categories || []
    };
  };

  // Create a review session with specific questions
  const createReviewSession = (studySetId, mode, questionIds) => {
    const studySet = studySets.find(set => set.id === studySetId);
    if (!studySet) return null;

    // Get only the questions with the specified IDs
    let questions = studySet.questions.filter(q => questionIds.includes(q.id));
    
    // Filter out questions without choices if mode is multiple-choice
    if (mode === 'multiple-choice') {
      questions = questions.filter(q => q.choices && q.choices.length > 0);
    }
    
    // Shuffle questions
    questions = shuffleArray(questions);

    // Format questions based on mode
    const formattedQuestions = questions.map(q => {
      const formatted = {
        id: q.id,
        question: q.question,
        category: q.category,
        difficulty: q.difficulty
      };

      if (mode === 'beginner' && q.hint) {
        formatted.hint = q.hint;
      }

      if (mode === 'multiple-choice' && q.choices) {
        formatted.choices = shuffleArray(q.choices);
      }

      return formatted;
    });

    return {
      sessionId: Date.now().toString(),
      mode,
      questions: formattedQuestions,
      totalQuestions: formattedQuestions.length,
      isReviewSession: true
    };
  };

  // Check answer
  const checkAnswer = (studySetId, questionId, userAnswer) => {
    const studySet = studySets.find(set => set.id === studySetId);
    if (!studySet) return { correct: false, correctAnswer: '', explanation: 'Study set not found' };

    const question = studySet.questions.find(q => q.id === questionId);
    if (!question) return { correct: false, correctAnswer: '', explanation: 'Question not found' };

    const correctAnswers = Array.isArray(question.answer) ? question.answer : [question.answer];
    const isCorrect = correctAnswers.some(answer => 
      answer.toLowerCase().trim() === userAnswer.toLowerCase().trim()
    );

    return {
      correct: isCorrect,
      correctAnswer: question.answer,
      explanation: question.explanation || null
    };
  };

  // Add a new study set (from file upload)
  const addStudySet = (studySet) => {
    // Validate study set structure
    if (!studySet.id || !studySet.title || !studySet.questions || !Array.isArray(studySet.questions)) {
      throw new Error('Invalid study set format');
    }

    // Check if study set already exists
    const existingIndex = studySets.findIndex(set => set.id === studySet.id);
    if (existingIndex >= 0) {
      const newSets = [...studySets];
      newSets[existingIndex] = studySet;
      setStudySets(newSets);
    } else {
      setStudySets([...studySets, studySet]);
    }
  };

  const value = {
    studySets,
    getStudySetsSummary,
    getStudySet,
    getCategories,
    createStudySession,
    createReviewSession,
    checkAnswer,
    addStudySet
  };

  return (
    <StudyContext.Provider value={value}>
      {children}
    </StudyContext.Provider>
  );
}

export function useStudy() {
  const context = useContext(StudyContext);
  if (!context) {
    throw new Error('useStudy must be used within a StudyProvider');
  }
  return context;
}
